"use client"

import { useCallback, useState } from "react"

/** How many rows a record's list draws before it asks. */
export const BATCH = 25

/**
 * A window onto a list that grows when the reader asks for more.
 *
 * Neither `GET /tasks` nor `GET /comments` takes a limit, an offset or a
 * cursor: every task and every comment on a candidate arrives in one response,
 * and there is no way to ask for fewer. So the paging that exists has to be
 * about what gets *drawn* — on a record worked for a year, mapping several
 * hundred rich-text comment previews into the DOM costs a visibly slower open,
 * for rows nobody scrolled to.
 *
 * Appending rather than paging, deliberately: these lists are read from the top
 * down, and one of them animates a row into the document it opens, which page
 * controls would fight. Search and filtering still run over everything fetched,
 * so narrowing never has to walk pages to find something.
 *
 * `resetKey` is whatever changes the list underneath the window — a search
 * term, a scope. The window returns to the first batch when it changes, because
 * a window held open over the previous list means nothing against the new one.
 */
export function useVisibleBatch<T>(
  rows: T[],
  resetKey: unknown = null,
  /**
   * An index that must be inside the window whatever its size.
   *
   * The comment rail draws itself beside an open comment, and the open one can
   * be the ninetieth: without this, opening it from an expanded list mounts a
   * fresh rail at the first batch and the comment you are reading is the one
   * row missing from the list of them.
   */
  ensureIndex?: number,
) {
  const [shown, setShown] = useState(BATCH)
  const [key, setKey] = useState(resetKey)

  /*
    Adjusted during render rather than in an effect — React's own pattern for
    state that depends on a prop. An effect would let one frame of the previous
    window paint over the new list before it corrected itself.
  */
  const stale = key !== resetKey
  if (stale) {
    setKey(resetKey)
    setShown(BATCH)
  }
  const chosen = stale ? BATCH : shown
  const limit =
    ensureIndex !== undefined && ensureIndex >= 0
      ? Math.max(chosen, ensureIndex + 1)
      : chosen

  /*
    Advance from what is actually on screen, not from the stored count. Where
    `ensureIndex` has already pushed the window past `shown` — an open comment
    at position 90 of 100 — incrementing the stored count would spend the first
    three clicks catching up to a window the reader is already looking at.
  */
  const showMore = useCallback(() => setShown(limit + BATCH), [limit])

  return {
    visible: rows.length <= limit ? rows : rows.slice(0, limit),
    /** How many are held back — 0 when the window covers everything. */
    remaining: Math.max(0, rows.length - limit),
    showMore,
  }
}
